import React from "react";
import { toast } from "sonner";
import { Loader } from "lucide-react";
import { BsArrowReturnLeft } from "react-icons/bs";
import { IoChatbubblesOutline } from "react-icons/io5";
import { FaRegCopy, FaRegFilePdf } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { RiCloseLargeFill } from "react-icons/ri";
import type { ReviewTileItem } from "@/types/tile.types";
import type { DismissAllTilesRequest } from "@/types/review.types";
import { useDismissAllTiles } from "@/hooks/useDismissAllTiles";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";

const ALLOWED_QUERIES_FOR_BULK_WRITE_IT = [
  "conflicting-terms",
  "complex-language",
  "missing-clauses",
  "points-to-negotiate",
  "undefined-terms",
  "ambiguities",
  "custom-review",
  "inconsistencies",
  "non-standard-clauses",
  "risk-analysis",
];

export function findUnsupportedBulkWriteItQueries(tiles: ReviewTileItem[]): string[] {
  const seen = new Set<string>();
  for (const tile of tiles) {
    const query = tile.query ?? "";
    if (query !== "" && !ALLOWED_QUERIES_FOR_BULK_WRITE_IT.includes(query)) {
      seen.add(query);
    }
  }
  return Array.from(seen);
}

export function makeBulkResultHandler(setDialogOpen?: (v: boolean) => void) {
  return {
    onComplete: (result: { success: number; failed: number }) => {
      if (result.failed > 0) {
        toast.warning(`Completed: ${result.success} successful, ${result.failed} failed`);
      } else {
        toast.success(`Successfully completed ${result.success} actions`);
      }
      if (setDialogOpen != null) {
        setTimeout(() => setDialogOpen(false), 1000);
      }
    },
    onError: (error: string) => {
      toast.error(error);
      setDialogOpen?.(false);
    },
  };
}

export function makeBulkCopyHandler() {
  return {
    onComplete: (result: { success: number; failed: number }) => {
      if (result.failed > 0) {
        toast.warning(`Completed: ${result.success} successful, ${result.failed} failed`);
      } else {
        toast.success(`Successfully copied ${result.success} tiles`);
      }
    },
    onError: (error: string) => {
      toast.error(error);
    },
  };
}

export function computeProgressInfo(progress: { completed: number; total: number }, label: string) {
  const percentage = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  const message =
    progress.completed < progress.total ? (
      <>
        Processed <b>{progress.completed}</b> of <b>{progress.total}</b> tiles...
      </>
    ) : (
      `Completed processing ${progress.total} ${label}.`
    );

  return { percentage, message };
}

export interface BulkActionItem {
  action: string;
  onClick: () => void;
  disabled: boolean;
  content: string;
  icon: React.ReactNode;
  loading: boolean;
  className: string;
}

export const BulkActionButtons: React.FC<{ actions: BulkActionItem[] }> = ({ actions }) => (
  <div className="flex items-center gap-3 flex-wrap">
    {actions.map((action) => (
      <TooltipProvider key={action.action}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              role="button"
              aria-disabled={action.disabled}
              className={cn(
                "size-auto p-0 has-[>svg]:p-0",
                action.className,
                action.disabled && "pointer-events-none opacity-50 cursor-not-allowed"
              )}
              onClick={action.disabled ? undefined : action.onClick}
            >
              {action.loading ? <Loader className="animate-spin" /> : action.icon}
            </div>
          </TooltipTrigger>
          <TooltipContent>{action.content}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ))}
  </div>
);

export interface BuildBulkActionsParams {
  impactText: string;
  disabled: boolean;
  bulkInsertLoading: boolean;
  addAsCommentsLoading: boolean;
  isCreatingDocument: boolean;
  bulkCopyLoading: boolean;
  bulkWriteItLoading: boolean;
  dismissLoading: boolean;
  handleBulkAction: (action: string) => Promise<void>;
  handleCloseAll: () => Promise<void>;
}

export function buildBulkActions(p: BuildBulkActionsParams): BulkActionItem[] {
  return [
    {
      action: "writeItAll",
      onClick: () => p.handleBulkAction("writeItAll"),
      disabled: p.disabled,
      content: `Insert ${p.impactText} tiles`,
      icon: <BsArrowReturnLeft className="size-3.5 text-neutral-400" />,
      loading: p.bulkInsertLoading,
      className: "",
    },
    {
      action: "addAsComments",
      onClick: () => p.handleBulkAction("addAsComments"),
      disabled: p.disabled,
      content: `Add comments for ${p.impactText} tiles`,
      icon: <IoChatbubblesOutline className="size-3.5 text-neutral-400" />,
      loading: p.addAsCommentsLoading,
      className: "",
    },
    {
      action: "createDocumentFromTiles",
      onClick: () => p.handleBulkAction("createDocumentFromTiles"),
      disabled: p.disabled,
      content: "Create a new Word document from all tiles",
      icon: <FaRegFilePdf className="size-3.5 text-neutral-400" />,
      loading: p.isCreatingDocument,
      className: "",
    },
    {
      action: "copyAllTiles",
      onClick: () => p.handleBulkAction("copyAllTiles"),
      disabled: p.disabled,
      content: `Copy ${p.impactText} tiles`,
      icon: <FaRegCopy className="size-3.5 text-neutral-400" />,
      loading: p.bulkCopyLoading,
      className: "",
    },
    {
      action: "bulkWriteIt",
      onClick: () => p.handleBulkAction("bulkWriteIt"),
      disabled: p.disabled,
      content: "Bulk Write It",
      icon: <FiEdit2 className="size-3.5 text-neutral-400" />,
      loading: p.bulkWriteItLoading,
      className: "",
    },
    {
      action: "closeAll",
      onClick: p.handleCloseAll,
      disabled: p.disabled,
      content: "Close all tiles",
      icon: <RiCloseLargeFill className="size-3.5 text-neutral-400" />,
      loading: p.dismissLoading,
      className: "text-danger-200",
    },
  ];
}

/**
 * Explicit shape of the hooks bundle consumed by executeBulkAction.
 * Defined here instead of via ReturnType<typeof useBulkActionsHooks> to avoid
 * a circular dependency between this utils file and TilesBulkActions.tsx.
 */
interface BulkActionsHooksShape {
  bulkInsertHook: {
    isProcessing: boolean;
    insertAllTiles: (tiles: ReviewTileItem[]) => Promise<void>;
  };
  addAsCommentsHook: {
    isProcessing: boolean;
    addAllAsComments: (tiles: ReviewTileItem[]) => Promise<void>;
  };
  bulkCopyHook: { isProcessing: boolean; copyAllTiles: (tiles: ReviewTileItem[]) => Promise<void> };
  createDocumentFromTiles: (tiles: ReviewTileItem[], title: string) => Promise<unknown>;
  mutateBulkWriteIt: (params: { items: ReviewTileItem[]; documentId: string }) => void;
  writeItProgress: { showProgress: () => void };
  dismissAllTilesMutation: ReturnType<typeof useDismissAllTiles>;
}

export async function executeBulkAction(
  action: string,
  filteredItems: ReviewTileItem[],
  documentId: string,
  hooks: BulkActionsHooksShape,
  setIsBulkInsertDialogOpen: (v: boolean) => void,
  setIsAddAsCommentsDialogOpen: (v: boolean) => void
) {
  if (filteredItems.length === 0) {
    toast.warning("No tiles to process");
    return;
  }

  try {
    switch (action) {
      case "writeItAll":
        setIsBulkInsertDialogOpen(true);
        await hooks.bulkInsertHook.insertAllTiles(filteredItems);
        break;
      case "addAsComments":
        setIsAddAsCommentsDialogOpen(true);
        await hooks.addAsCommentsHook.addAllAsComments(filteredItems);
        break;
      case "createDocumentFromTiles":
        await hooks.createDocumentFromTiles(filteredItems, "MS Word Document Review Items");
        break;
      case "copyAllTiles":
        await hooks.bulkCopyHook.copyAllTiles(filteredItems);
        break;
      case "bulkWriteIt": {
        const unsupportedQueries = findUnsupportedBulkWriteItQueries(filteredItems);
        if (unsupportedQueries.length > 0) {
          const list = unsupportedQueries.map((q) => `"${q.replace(/-/g, " ")}"`).join(", ");
          toast.warning("Invalid queries selected", {
            description: `Please unselect the following queries that are not supported for Bulk Write It: ${list}`,
            duration: 10000,
          });
          return;
        }
        hooks.writeItProgress.showProgress();
        hooks.mutateBulkWriteIt({ items: filteredItems, documentId });
        break;
      }
      default:
        console.warn("Unknown action:", action);
    }
  } catch (error) {
    console.error(`Error in bulk action ${action}:`, error);
  }
}

export function executeCloseAll(
  filteredItems: ReviewTileItem[],
  documentId: string,
  selectedQueryTerms: string[],
  dismissAllTilesMutation: ReturnType<typeof useDismissAllTiles>
) {
  if (filteredItems.length === 0) {
    toast.warning("No tiles to dismiss");
    return;
  }

  const request: DismissAllTilesRequest = {
    document_id: [documentId],
    query_term: selectedQueryTerms,
  };

  dismissAllTilesMutation.mutate(request, {
    onSuccess: () => toast.success("All tiles dismissed successfully"),
    onError: (error) => toast.error("Error dismissing all tiles:", { description: error.message }),
  });
}
